import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { ErrorCode } from 'react-dropzone';
import { useMutation, useQueryClient } from 'react-query';
import { useDispatch, useSelector } from 'react-redux';

import { BestOfSubmissionWriteResponse } from 'app/features/BestOf/types';
import dayjs from 'dayjs';
import { Formik, FormikProps } from 'formik';
import useBehavioral, { BEHAVIORALS } from 'hooks/useBehavioral';
import useIsMobile from 'hooks/useIsMobile';
import useUserData from 'hooks/useUserData';
import { getImageInfo } from 'services/app';
import { BestOfApiError, unwrapBestOfResponse } from 'services/best-of';
import { ResourceUploadError, withUploadedResourceFiles } from 'services/resource-uploads';
import { getResourceValidate, postResource } from 'services/resources';
import { closeResourceModal } from 'store/reducers/global/global-reducer';
import {
  formDataSelector,
  isFormDirtySelector,
  setIsFormDirty,
  setIsSuccess,
  setShowResourceSubmissionModal,
} from 'store/reducers/resource-submission/resource-submission-reducer';
import { draftUuidsSelector } from 'store/reducers/resources/resources-reducer';
import { ThemeProvider } from 'styled-components';
import { htmlToPlainString } from 'utils';
import * as Yup from 'yup';

import ClientSelect from '../CommonStyled/ClientSelect';
import CompanySelect from '../CommonStyled/CompanySelect';
import DateInput from '../CommonStyled/DateInput';
import FileInput from '../CommonStyled/FileInput';
import FormikListener from '../CommonStyled/FormikListener';
import PersonSelect from '../CommonStyled/PersonSelect';
import ProjectCategorySelect from '../CommonStyled/ProjectCategorySelect';
import SkillSelect from '../CommonStyled/SkillSelect';
import TextInput from '../CommonStyled/TextInput';
import usePeopleSamples from '../People/hooks/usePeopleSamples';
import { ResourceSubmissionModalContext } from '../ResourceSubmissionModal/ResourceSubmissionModalContext';
import ResourceCard, { ResourceFormat } from '../Resources/ResourceCard';
import {
  CheckboxCheckIcon,
  KyuLaurelBadgeIcon,
  LeftArrow,
  ResourceIcon,
  Spinner,
} from '../shared/icons';
import ResourceSubmissionCategory from './ResourceSubmissionCategory';
import { ResourceSubmissionContext } from './ResourceSubmissionContext';
import ResourceSubmissionFailure from './ResourceSubmissionFailure';
import ResourceSubmissionPaywallAccess from './ResourceSubmissionPaywallAccess';
import ResourceSubmissionLinkHint from './ResourceSubmissionLinkHint';
import ResourceSubmissionPermissions from './ResourceSubmissionPermissions';
import ResourceSubmissionPreview from './ResourceSubmissionPreview';
import ResourceSubmissionTagsField from './ResourceSubmissionTagsField';
import ResourceSubmissionType from './ResourceSubmissionType';
import ResourceSubmissionTypeSelect from './ResourceSubmissionTypeSelect';
import ResourceSubmissionUnpublishDialog from './ResourceSubmissionUnpublishDialog';
import {
  BestOfSubmissionFormContext,
  BestOfSubmissionIntent,
  resolveBestOfSubmissionCategoryUuid,
  withBestOfSubmissionMetadata,
} from './bestOfSubmissionContext';
import {
  getRules,
  isShown,
  isRequired,
  getFieldTitle,
  RESOURCE_FORMAT_NAME,
} from './config/resourceTypeRules';
import { useResourceSubmissionFields } from './hooks/useResourceSubmissionFields';
import {
  ResourceSubmissionContainer,
  ResourceSubmissionWrapper,
  ResourceSubmissionFormWrapper,
  ResourceSubmissionFormFieldWrapper,
  ResourceSubmissionFieldTitle,
  ResourceSubmissionFormFieldContainer,
  ResourceSubmissionFieldSubtitle,
  ResourceSubmissionFormFieldsWrapper,
  ResourceSubmissionCardContainer,
  ResourceSubmissionFieldCheckboxContainer,
  ResourceSubmissionFieldCheckbox,
  ResourceSubmissionFieldCheckboxBackground,
  ResourceSubmissionFieldSubmitButton,
  ResourceSubmissionFieldSubmitButtonLabel,
  ResourceSubmissionFieldSubmitButtonContainer,
  ResourceSubmissionSubmitError,
  ResourceSubmissionFieldCheckboxWrapper,
  ResourceSubmissionFieldCheckboxLabel,
  ResourceSubmissionFieldInitiativeContainer,
  ResourceSubmissionTitle,
  ResourceSubmissionTitleContainer,
  ResourceSubmissionBestOfTitleContainer,
  ResourceSubmissionBestOfTitle,
  ResourceSubmissionBackButton,
  ResourceSubmissionSectionTitle,
  ResourceSubmissionStepsWrapper,
  ResourceSubmissionFieldUnpublishButton,
  ResourceSubmissionFieldUnpublishButtonLabel,
  ResourceSubmissionFieldUnpublishTitle,
  ResourceSubmissionFieldUnpublishWrapper,
} from './styled';
import { normalizeSubmissionContributor } from './contributorPresentation';
import { buildResourceSubmissionSchema } from './validation/buildResourceSubmissionSchema';

const serverValidationMap = new Map<string, string>([
  [
    'This link is already set in another resource.',
    'It looks like a resource with that URL already exists. Please try another one.',
  ],
]);

const isValidationErrorPayload = (value: unknown): value is Record<string, string[]> =>
  Boolean(
    value &&
      typeof value === 'object' &&
      Object.values(value).every(
        (messages) =>
          Array.isArray(messages) && messages.every((message) => typeof message === 'string')
      )
  );

export const RESOURCE_SUBMISSION_FINISHED_ACTION = 'RESOURCE_SUBMISSION_FINISHED';

export const ENTRY_TYPE = {
  LINK: 'url',
  VIMEO_YOUTUBE: 'video',
  READYMAG: 'readymag',
  GOOGLE_SLIDES: 'google slides',
  GOOGLE_STUDIO: 'google studio',
  IMAGE: 'image',
  PDF: 'pdf',
  KYU_OS: 'kyu os',
};

export interface IFile {
  base64: string;
  filename: string;
  content_type: string;
}

export type ENTRY_TYPE = (typeof ENTRY_TYPE)[keyof typeof ENTRY_TYPE];

interface ResourceSubmissionProps {
  existingResource?: any;
  onSubmissionSuccess?: () => void;
  onBack?: () => void;
  bestOfYear?: number;
  bestOfSubmission?: BestOfSubmissionFormContext;
}

export default function ResourceSubmission({
  existingResource,
  onSubmissionSuccess,
  onBack,
  bestOfYear,
  bestOfSubmission,
}: ResourceSubmissionProps = {}) {
  const isMobile = useIsMobile();
  const dispatch = useDispatch();
  const userData = useUserData();
  const queryClient = useQueryClient();
  const resourceCreatedBehavioralFunction = useBehavioral(
    BEHAVIORALS.BEST_OF_2025_SUBMISSION_PHASE_RESOURCE_CREATED
  );
  const resourceEditedBehavioralFunction = useBehavioral(
    BEHAVIORALS.BEST_OF_2025_SUBMISSION_PHASE_RESOURCE_EDITED
  );
  const resourceWithdrawnBehavioralFunction = useBehavioral(
    BEHAVIORALS.BEST_OF_2025_SUBMISSION_PHASE_RESOURCE_WITHDRAWN
  );
  const bestOfResourceCreatedBehavioralFunction = useBehavioral(
    BEHAVIORALS.BEST_OF_SUBMISSION_PHASE_RESOURCE_CREATED
  );
  const bestOfResourceEditedBehavioralFunction = useBehavioral(
    BEHAVIORALS.BEST_OF_SUBMISSION_PHASE_RESOURCE_EDITED
  );
  const isFormDirty = useSelector(isFormDirtySelector);
  const formData = useSelector(formDataSelector);
  const fromBestOf2025 = bestOfYear === 2025;
  const isReadOnly = Boolean(bestOfSubmission?.readOnly);
  const readOnlyProps = isReadOnly ? { readOnly: true } : {};
  const disabledProps = isReadOnly ? { disabled: true } : {};
  const isBestOfSubmission = Boolean(bestOfYear);
  const isV2BestOfSubmission = isBestOfSubmission && !fromBestOf2025;
  const imageMaxSizeBytes = 5000000;
  const draftUuids = useSelector(draftUuidsSelector);
  const { setConfirmClose } = useContext(ResourceSubmissionModalContext);
  const {
    data,
    isLoading: fieldsLoading,
    isError: fieldsError,
  } = useResourceSubmissionFields(bestOfYear);
  const formikRef = useRef<FormikProps<any>>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isError, setIsError] = useState<boolean>(false);
  const [responseStatus, setResponseStatus] = useState<number>(null);
  const testedTitles = useRef<string[]>([]);
  const invalidTitles = useRef<string[]>([]);
  const testedLinks = useRef<string[]>([]);
  const invalidLinks = useRef<string[]>([]);
  const lastValidatedTitle = useRef<string | null>(null);
  const lastValidatedLink = useRef<string | null>(null);
  const [prePopulatedFiles, setPrePopulatedFiles] = useState<string[]>(
    existingResource &&
      [ResourceFormat.PDF, ResourceFormat.IMAGE].includes(existingResource.resource_format_name) &&
      (existingResource.file || existingResource.link)
      ? [existingResource.file || existingResource.link]
      : []
  );
  const [prePopulatedImages, setPrePopulatedImages] = useState<string[]>(
    existingResource &&
      (existingResource.image ||
        (existingResource.additional_images && existingResource.additional_images?.length > 0))
      ? [existingResource.image, ...existingResource.additional_images]
      : []
  );
  const [currentContributors, setCurrentContributors] = useState<any[]>(
    existingResource?.contributors.people || [
      { name: `${userData?.first_name} ${userData?.last_name}`, uuid: userData?.uuid },
    ]
  );
  const [companies, setCompanies] = useState<any[]>(
    existingResource?.contributors.companies || [
      {
        name: userData?.kyu_company?.name,
        slug: userData?.kyu_company?.slug,
        uuid: userData?.kyu_company?.uuid ?? userData?.kyu_company?.id,
      },
    ]
  );
  const [clients, setClients] = useState<any[]>(existingResource?.clients || []);
  const [resourceFormat, setResourceFormat] = useState<any>(
    existingResource?.resource_format_name || null
  );
  const { data: peopleSamples, isLoading: isPeopleSamplesLoading } = usePeopleSamples(
    formikRef.current?.values['contributors_uuids'] || []
  );
  const [resource, setResource] = useState<any>(
    existingResource
      ? {
          ...existingResource,
          image: existingResource.image ? [existingResource.image] : [],
          additional_images: existingResource.additional_images
            ? [existingResource.additional_images]
            : [],
          file:
            [ResourceFormat.PDF, ResourceFormat.IMAGE].includes(existingResource.resource_format_name)
              ? [existingResource.file || existingResource.link].filter(Boolean)
              : [],
          clients_uuids: existingResource.clients.map((client) => client.uuid),
          project_categories_uuids: existingResource.project_categories.map(
            (projectCategory) => projectCategory.uuid
          ),
          skills_uuids: existingResource.skills.map((skill) => skill.uuid),
          contributors_uuids: existingResource.contributors.people.map((person) => person.uuid),
          companies_uuids: existingResource.contributors.companies.map((company) => company.uuid),
          draft: existingResource.status === 'draft',
        }
      : null
  );
  const [isPreviewing, setIsPreviewing] = useState<boolean>(false);
  const onPreviewChange = bestOfSubmission?.onPreviewChange;
  useEffect(() => {
    onPreviewChange?.(isPreviewing);
  }, [isPreviewing, onPreviewChange]);
  const [skills, setSkills] = useState<any[]>(existingResource?.skills || []);
  const [projectCategories, setProjectCategories] = useState<any[]>(
    existingResource?.project_categories || []
  );
  const [isPublishing, setIsPublishing] = useState<boolean>(false);
  const [isPublishDialogOpen, setIsPublishDialogOpen] = useState<boolean>(false);
  const [isUnpublishing, setIsUnpublishing] = useState<boolean>(false);
  const [isUnpublishDialogOpen, setIsUnpublishDialogOpen] = useState<boolean>(false);

  const initialValues = {
    resource_uuid: existingResource?.uuid ?? formData?.uuid ?? null,
    resource_submission_category_uuid: resolveBestOfSubmissionCategoryUuid(
      existingResource,
      bestOfSubmission,
      formData
    ),
    resource_type: isBestOfSubmission
      ? data?.resource_submission_categories?.find((category) => category.uuid ===
          resolveBestOfSubmissionCategoryUuid(existingResource, bestOfSubmission, formData))?.resource_type_slug
        || (existingResource?.resource_type === 'pitch' ? 'pitch' : RESOURCE_FORMAT_NAME.CASE_STUDY)
      : existingResource?.resource_type ?? formData?.resource_type ?? RESOURCE_FORMAT_NAME.NOT_SURE,
    title: existingResource?.title ?? formData?.title ?? null,
    short_description: existingResource?.short_description
      ? htmlToPlainString(existingResource?.short_description)
      : formData?.short_description ?? null,
    long_description: existingResource?.long_description
      ? htmlToPlainString(existingResource?.long_description)
      : formData?.long_description ?? null,
    resource_format_uuid:
      existingResource?.resource_format_uuid ?? formData?.resource_format_uuid ?? null,
    tags: existingResource?.tags ?? formData?.tags ?? [],
    link: existingResource?.link ?? formData?.link ?? null,
    file: formData?.file ?? null,
    image: formData?.image ?? null,
    additional_images: formData?.additional_images ?? null,
    contributors_uuids:
      existingResource?.contributors.people.map((person) => person.uuid) ?? [userData?.uuid] ?? [],
    companies_uuids:
      existingResource?.contributors.companies.map((company) => company.uuid) ??
      formData?.companies_uuids ??
      [userData?.kyu_company?.uuid ?? userData?.kyu_company?.id].filter(Boolean),
    show_contributor: true,
    pulse_submission: existingResource?.pulse_submission ?? formData?.pulse_submission ?? false,
    draft: existingResource?.draft ?? formData?.draft ?? false,
    internal_initiative: existingResource
      ? existingResource?.clients.length === 0
      : formData?.internal_initiative ?? !isBestOfSubmission,
    clients_uuids:
      existingResource?.clients.map((client) => client.uuid) ?? formData?.clients_uuids ?? [],
    project_categories_uuids:
      existingResource?.project_categories.map((projectCategory) => projectCategory.uuid) ??
      formData?.project_categories_uuids ??
      [],
    started_at: existingResource?.started_at ?? formData?.started_at ?? null,
    released_at: existingResource?.released_at ?? formData?.released_at ?? null,
    internal_share_status:
      existingResource?.internal_share_status ?? formData?.internal_share_status ?? 'open',
    external_client_share_status:
      existingResource?.external_client_share_status ??
      formData?.external_client_share_status ??
      'denied',
    sharing_notes: existingResource?.sharing_notes ?? formData?.sharing_notes ?? '',
    skills_uuids:
      existingResource?.skills?.map((skill) => skill.uuid) ?? formData?.skills_uuids ?? [],
    impact_metrics: existingResource?.impact_metrics ?? formData?.impact_metrics ?? null,
    has_paywall: existingResource?.has_paywall ?? formData?.has_paywall ?? false,
  };
  const [invalidValues, setInvalidValues] = useState<{
    [key: string]: { value: string; message: string }[];
  }>({});

  const setFieldErrorsFromResponse = (payload: { [key: string]: string[] }) => {
    let newInvalidValues = { ...invalidValues };

    Object.keys(payload).forEach((key) => {
      formikRef.current.setFieldError(key, payload[key][0]);
      if (newInvalidValues[key]) {
        newInvalidValues[key].push({
          value: formikRef.current.values[key],
          message: serverValidationMap.has(payload[key][0])
            ? serverValidationMap.get(payload[key][0])
            : payload[key][0],
        });
      } else {
        newInvalidValues[key] = [
          {
            value: formikRef.current.values[key],
            message: serverValidationMap.has(payload[key][0])
              ? serverValidationMap.get(payload[key][0])
              : payload[key][0],
          },
        ];
      }
    });

    setInvalidValues(newInvalidValues);
  };

  useEffect(() => {
    if (peopleSamples) {
      const people = peopleSamples.people.map(normalizeSubmissionContributor);
      setCurrentContributors(people);
      setResource({
        ...resource,
        contributors: {
          people,
          companies: resource?.contributors?.companies || [],
        },
      });
    } else if (formikRef.current?.values['contributors_uuids'].length === 0) {
      setCurrentContributors([]);
      setResource({
        ...resource,
        contributors: {
          people: [],
          companies: resource?.contributors?.companies || [],
        },
      });
    }
  }, [peopleSamples]);

  useEffect(() => {
    if (formikRef.current) {
      formikRef.current.validateForm();
    }
  }, [invalidValues]);

  const persist = async ({
    values,
    intent,
  }: {
    values: Record<string, unknown>;
    intent: BestOfSubmissionIntent;
  }) => {
    const serialized = serialize(values);
    const payload = bestOfSubmission
      ? withBestOfSubmissionMetadata(serialized, bestOfSubmission, intent)
      : bestOfYear
      ? { ...serialized, best_of: bestOfYear }
      : serialized;
    if (!bestOfSubmission) return postResource(await withUploadedResourceFiles(payload));

    try {
      const uploaded = await withUploadedResourceFiles(payload);
      const response = await unwrapBestOfResponse<BestOfSubmissionWriteResponse>(
        postResource(uploaded)
      );
      bestOfSubmission.onSaved(response);
      if (intent === 'submit') {
        const resourceUuid = response.submission.resource.uuid;
        if (existingResource || bestOfSubmission.submissionUuid) {
          bestOfResourceEditedBehavioralFunction({
            year: bestOfSubmission.year,
            related_object_uuid: resourceUuid,
          });
        } else {
          bestOfResourceCreatedBehavioralFunction({ year: bestOfSubmission.year });
        }
      }
      return response;
    } catch (error) {
      const apiError =
        error instanceof ResourceUploadError
          ? new BestOfApiError(error.message, 0, 'upload_failed')
          : (error as BestOfApiError);
      const validationErrors = apiError.details?.validation_errors;
      if (isValidationErrorPayload(validationErrors)) {
        setFieldErrorsFromResponse(validationErrors);
        setIsPreviewing(false);
      }
      bestOfSubmission.onFailure(apiError);
      throw apiError;
    }
  };

  const { mutate } = useMutation(persist, {
    onSettled: (data, error) => {
      setIsLoading(false);
      if (bestOfSubmission) {
        if (!error) setConfirmClose(false);
        return;
      }

      if (error) return;

      setConfirmClose(false);
      const resourceResponse = data as Awaited<ReturnType<typeof postResource>>;
      if (resourceResponse.ok) {
        if (!existingResource) {
          dispatch(setIsSuccess(true));
        }
      } else if (resourceResponse.data.error.table?.status === 422) {
        setFieldErrorsFromResponse(resourceResponse.data.error.table.payload);
        if (isPreviewing) {
          setIsPreviewing(false);
        }
      } else {
        setIsError(true);
        setResponseStatus(resourceResponse.status);
      }

      if (onSubmissionSuccess) {
        onSubmissionSuccess();
      }

      if (existingResource) {
        resourceEditedBehavioralFunction({
          related_object_uuid: existingResource.uuid,
        });
      } else {
        resourceCreatedBehavioralFunction();
      }

      window.postMessage(RESOURCE_SUBMISSION_FINISHED_ACTION, '*');
      if (existingResource) {
        queryClient.invalidateQueries({ queryKey: [`resource-${existingResource.slug}`] });
      }
    },
    onError: () => {
      setIsLoading(false);
      if (bestOfSubmission) {
        setIsPreviewing(false);
        return;
      }

      setIsError(true);
      setResponseStatus(500);
    },
    onMutate: () => setIsLoading(true),
  });
  const ResourceFormatOptions = useMemo(() => {
    if (!data) return [];

    return data.resource_formats
      .filter((resourceFormat) => resourceFormat.name.toLowerCase() !== 'embeddable')
      .map((resourceFormat) => ({
        value: resourceFormat.uuid,
        label: resourceFormat.name,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [data]);

  const valuesToResource = (values) => {
    return {
      ...values,
      image: values.image ? values.image[0]?.base64 : null,
      additional_images:
        values.additional_images && values.additional_images?.length > 0
          ? values.additional_images.map((image) => image.base64)
          : null,
      link: values.file ? values.file[0]?.base64 : values.link,
      skills,
      resource_format_name: resourceFormat,
      project_categories: projectCategories,
      clients,
      contributors: {
        people: currentContributors,
        companies,
      },
    };
  };

  const onFormikChange = (values) => {
    setResource(valuesToResource(values));
    if (values.image) {
      setPrePopulatedImages(values.image);
    }
    if (values.file) {
      setPrePopulatedFiles(values.file);
    }
  };

  const resetScroll = () => {
    const resourceModalWrapper = document.querySelector('[data-id="resource-modal-container"]');
    const resourceSubmissionContentWrapper = document.querySelector(
      '[data-id="resource-submission-content-wrapper"]'
    );

    if (resourceModalWrapper) {
      resourceModalWrapper.scrollTo(0, 0);
    }

    if (resourceSubmissionContentWrapper) {
      resourceSubmissionContentWrapper.scrollTo(0, 0);
    }
  };

  const currentResourceFormat = useMemo<ENTRY_TYPE>(() => {
    if (!formikRef.current?.values['resource_format_uuid'] || !data) return null;

    return data.resource_formats
      .find(
        (ResourceFormat) =>
          ResourceFormat.uuid === formikRef.current?.values['resource_format_uuid']
      )
      ?.name.toLowerCase();
  }, [formikRef.current?.values['resource_format_uuid'], data]);

  // Compute rules based on resource_type
  // Note: This will be recomputed when resource_type changes via Formik values
  const getCurrentRules = () => {
    const resourceType = formikRef.current?.values['resource_type'];
    return getRules(resourceType, isBestOfSubmission);
  };

  const handleSubmitClick = async () => {
    const errors = await formikRef.current.validateForm();
    if (Object.keys(errors).length) {
      Object.keys(errors).forEach((key) => {
        formikRef.current.setFieldTouched(key, true);
      });

      const orderOfFields = [
        'resource_submission_category_uuid',
        'title',
        'short_description',
        'image',
        'resource_format_uuid',
        'file',
        'link',
        'clients_uuids',
        'started_at',
        'released_at',
        'project_categories_uuids',
        'impact_metrics',
        'long_description',
        'tags',
        'skills_uuids',
        'contributors_uuids',
        'companies_uuids',
        'pulse_submission',
        'internal_share_status',
        'external_client_share_status',
        'internal_initiative',
        'draft',
        'additional_images',
      ];

      const firstError = orderOfFields.find((key) => errors[key]);

      if (firstError) {
        const element = document.querySelector(`[name="${firstError}"]`) as HTMLInputElement;

        if (!element) return;

        if (element.type === 'hidden') {
          element.parentElement.scrollIntoView({ behavior: 'smooth' });
        } else {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }

      return;
    }

    if (!isPreviewing) {
      resetScroll();

      setIsPreviewing(true);
    } else {
      formikRef.current.submitForm();
    }
  };

  const handleInitiativeCheckboxChange = (state: boolean) => {
    if (isReadOnly) return;

    if (!formikRef.current.touched.internal_initiative) {
      formikRef.current.setFieldTouched('internal_initiative', true);
    }
    formikRef.current.setFieldValue('internal_initiative', state);
  };

  const serialize = (values) => {
    const valuesCopy = { ...values };
    if (typeof values.short_description === 'string' && values.short_description.length > 0) {
    valuesCopy.short_description = `<p>${values.short_description}</p>`;
    }
    if (typeof values.long_description === 'string' && values.long_description.length > 0) {
    valuesCopy.long_description = `<p>${values.long_description}</p>`;
    }

    if (isBestOfSubmission) {
      valuesCopy.resource_type = data?.resource_submission_categories?.find(
        (category) => category.uuid === valuesCopy.resource_submission_category_uuid
      )?.resource_type_slug || valuesCopy.resource_type;
    }
    if (valuesCopy.resource_type) {
      const resourceType = data?.resource_types.find(
        (resourceType) => resourceType.slug === valuesCopy.resource_type
      );
      valuesCopy.resource_type_uuid = resourceType?.uuid;
    }

    const normalizedFormat = currentResourceFormat?.toLowerCase();
    if (normalizedFormat === ENTRY_TYPE.LINK) {
      valuesCopy.file = null;
    } else if ([ENTRY_TYPE.IMAGE, ENTRY_TYPE.PDF].includes(normalizedFormat)) {
      valuesCopy.link = null;
    }

    const images =
      valuesCopy.image === existingResource?.image
        ? []
        : Array.isArray(valuesCopy.image)
        ? valuesCopy.image
        : [];
    if (images.length > 1) {
      valuesCopy.image = images[0];
      valuesCopy.additional_images = images.slice(1);
    } else {
      valuesCopy.image = images[0] ?? null;
    }

    if (valuesCopy.started_at) {
      valuesCopy.started_at = new Date(valuesCopy.started_at).toISOString();
    }
    if (valuesCopy.released_at) {
      valuesCopy.released_at = new Date(valuesCopy.released_at).toISOString();
    }

    const files = Array.isArray(valuesCopy.file) ? valuesCopy.file : [];
    return {
      ...valuesCopy,
      file: files[0] ?? null,
      additional_images: valuesCopy.additional_images || [],
    };
  };

  const handleSubmit = (values) => {
    mutate({ values, intent: 'submit' });
  };

  const resourceValidationSchema = useMemo(() => {
    const currentRules = getCurrentRules();
    return buildResourceSubmissionSchema({
      entryType: currentResourceFormat,
      rules: currentRules,
      existingResource,
      bestOfYear,
      testedTitles,
      invalidTitles,
      testedLinks,
      invalidLinks,
      invalidValues,
      lastValidatedTitle,
      lastValidatedLink,
    });
  }, [
    currentResourceFormat,
    formikRef.current?.values['resource_type'],
    existingResource,
    bestOfYear,
    testedTitles,
    invalidTitles,
    testedLinks,
    invalidLinks,
    invalidValues,
  ]);

  const fileAcceptance = useMemo(() => {
    if (currentResourceFormat?.toLowerCase() === ENTRY_TYPE.PDF) {
      return { 'application/pdf': ['.pdf'] };
    }

    if (currentResourceFormat?.toLowerCase() === ENTRY_TYPE.IMAGE) {
      return { 'image/jpeg': ['.jpeg', '.jpg', '.png', '.gif'] };
    }

    return {
      'image/jpeg': ['.jpeg', '.jpg', '.png', '.gif'],
      'application/pdf': ['.pdf'],
    };
  }, [currentResourceFormat]);

  const fileInvalidTypeError = useMemo(() => {
    if (currentResourceFormat?.toLowerCase() === ENTRY_TYPE.PDF) {
      return 'We can only accept PDFs. Try changing the file type instead.';
    }

    if (currentResourceFormat?.toLowerCase() === ENTRY_TYPE.IMAGE) {
      return 'We can only accept images (PNGs and JPGs)';
    }

    return 'We can only accept PDFs, images (PNGs and JPGs) and GIFs';
  }, [currentResourceFormat]);

  useEffect(() => {
    if (formikRef.current) {
      if ([ENTRY_TYPE.PDF, ENTRY_TYPE.IMAGE].includes(currentResourceFormat)) {
        formikRef.current.setFieldValue('link', null);
        formikRef.current.setFieldError('link', null);
      } else {
        formikRef.current.setFieldValue('file', null);
        formikRef.current.setFieldError('file', null);
      }
    }
  }, [currentResourceFormat]);

  // Sanitize hidden fields when resource_type changes
  useEffect(() => {
    if (formikRef.current) {
      const currentRules = getCurrentRules();
      const toStrip = currentRules.stripOnHide ?? [];
      toStrip.forEach((fieldName) => {
        formikRef.current.setFieldValue(fieldName, null);
        formikRef.current.setFieldError(fieldName, undefined);
        formikRef.current.setFieldTouched(fieldName, false);
      });
    }
  }, [formikRef.current?.values['resource_type']]);

  const canSubmitToPulse = (companyUuids: string[] = []) =>
    companyUuids.some((companyUuid) =>
      data?.pulse_companies?.some((pulseCompany) => pulseCompany.uuid === companyUuid)
    );

  const handleCompanySelectChange = (selectedCompanies) => {
    setCompanies(selectedCompanies);
    const selectedCompanyUuids = selectedCompanies
      .map((company) => company.uuid ?? company.id)
      .filter(Boolean);
    if (!canSubmitToPulse(selectedCompanyUuids)) {
      formikRef.current?.setFieldValue('pulse_submission', false);
    }
  };

  const handleClientSelectChange = (selectedClients) => {
    setClients(selectedClients);
  };

  const handleSkillSelectChange = (selectedSkills) => {
    setSkills(selectedSkills);
  };

  const handleProjectCategorySelectChange = (selectedProjectCategories) => {
    setProjectCategories(selectedProjectCategories);
  };

  useEffect(() => {
    if (formikRef.current && formikRef.current?.dirty && !isFormDirty) {
      dispatch(setIsFormDirty(true));
    }
  }, [formikRef.current?.dirty]);

  const handleUnpublishResource = () => {
    resourceWithdrawnBehavioralFunction({
      related_object_uuid: existingResource.uuid,
    });
    setIsUnpublishDialogOpen(false);
    setIsUnpublishing(true);
    postResource({
      ...existingResource,
      clients_uuids: existingResource.clients?.map((client) => client.uuid),
      project_categories_uuids: existingResource.project_categories?.map(
        (projectCategory) => projectCategory.uuid
      ),
      skills_uuids: existingResource.skills?.map((skill) => skill.uuid),
      contributors_uuids: existingResource.contributors.people?.map((person) => person.uuid),
      companies_uuids: existingResource.contributors.companies?.map((company) => company.uuid),
      resource_uuid: existingResource.uuid,
      draft: true,
    })
      .then((res) => {
        setIsUnpublishing(false);
      })
      .catch((err) => {
        setIsUnpublishing(false);
      })
      .finally(() => {
        setIsUnpublishing(false);
        queryClient.invalidateQueries({ queryKey: ['my-resources'] });
        window.postMessage(RESOURCE_SUBMISSION_FINISHED_ACTION, '*');
        dispatch(setShowResourceSubmissionModal(false));
        dispatch(closeResourceModal({}));
      });
  };

  const handlePublishResource = () => {
    setIsPublishDialogOpen(false);
    setIsPublishing(true);
    postResource({
      ...existingResource,
      clients_uuids: existingResource.clients?.map((client) => client.uuid),
      project_categories_uuids: existingResource.project_categories?.map(
        (projectCategory) => projectCategory.uuid
      ),
      skills_uuids: existingResource.skills?.map((skill) => skill.uuid),
      contributors_uuids: existingResource.contributors.people?.map((person) => person.uuid),
      companies_uuids: existingResource.contributors.companies?.map((company) => company.uuid),
      resource_uuid: existingResource.uuid,
      draft: false,
    })
      .then((res) => {
        setIsPublishing(false);
      })
      .catch((err) => {
        setIsPublishing(false);
      })
      .finally(() => {
        setIsPublishing(false);
        queryClient.invalidateQueries({ queryKey: ['my-resources'] });
        window.postMessage(RESOURCE_SUBMISSION_FINISHED_ACTION, '*');
        dispatch(setShowResourceSubmissionModal(false));
        dispatch(closeResourceModal({}));
      });
  };

  const submissionContextValue = {
    resource,
    submissionCategory: bestOfSubmission
      ? data?.resource_submission_categories.find(
          (category) =>
            category.uuid === formikRef.current?.values.resource_submission_category_uuid
        )
      : undefined,
    setResource,
    isPreviewing,
    setIsPreviewing,
    isError,
    setIsError,
    bestOfYear,
    readOnly: Boolean(bestOfSubmission?.readOnly),
  };

  if (isError) {
    return (
      <ThemeProvider theme={{ fromBestOf2025 }}>
        <ResourceSubmissionContext.Provider value={submissionContextValue}>
          <ResourceSubmissionWrapper>
            <ResourceSubmissionFailure status={responseStatus} />
          </ResourceSubmissionWrapper>
        </ResourceSubmissionContext.Provider>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider
      theme={{ fromBestOf2025, isUnpublishDialogOpen, existingResource: !!existingResource }}
    >
      <ResourceSubmissionContext.Provider value={submissionContextValue}>
        <ResourceSubmissionStepsWrapper>
          <ResourceSubmissionWrapper>
            <Formik
              innerRef={formikRef}
              initialValues={initialValues}
              validationSchema={resourceValidationSchema}
              onSubmit={handleSubmit}
              validateOnChange={true}
              validateOnBlur={true}
            >
              {(props) => {
                // Compute rules based on current resource_type - this will update when resource_type changes
                const currentRules = getRules(props.values['resource_type'], isBestOfSubmission);
                const canSeePulseCheckbox = canSubmitToPulse(props.values['companies_uuids']);
                return (
                  <>
                    {!isPreviewing ? (
                      <ResourceSubmissionContainer>
                        {!isBestOfSubmission ? (
                          <ResourceSubmissionTitleContainer>
                            <ResourceIcon width={40} height={40} fill="var(--color-primary)" />
                            <ResourceSubmissionTitle>Submit a Resource</ResourceSubmissionTitle>
                          </ResourceSubmissionTitleContainer>
                        ) : (
                          <ResourceSubmissionBestOfTitleContainer>
                            <ResourceSubmissionBestOfTitle>
                              Best of {bestOfYear}{isV2BestOfSubmission ? ':' : ' -'} Your Submission
                            </ResourceSubmissionBestOfTitle>
                            <KyuLaurelBadgeIcon />
                          </ResourceSubmissionBestOfTitleContainer>
                        )}
                        <div
                          data-testid="resource-submission-fields"
                          aria-disabled={isReadOnly || undefined}
                          style={{ display: 'contents' }}
                          onClickCapture={(event) => {
                            if (!isReadOnly) return;
                            const target = event.target as HTMLElement;
                            if (target.matches('input[readonly], textarea[readonly]')) return;
                            event.preventDefault();
                            event.stopPropagation();
                          }}
                          onDropCapture={(event) => {
                            if (!isReadOnly) return;
                            event.preventDefault();
                            event.stopPropagation();
                          }}
                        >
                        <ResourceSubmissionFormWrapper>
                          <ResourceSubmissionFormFieldsWrapper>
                            <FormikListener
                              onChange={onFormikChange}
                              onDirtyChange={bestOfSubmission ? setConfirmClose : undefined}
                            />
                            <ResourceSubmissionSectionTitle>
                              1. The Basics
                            </ResourceSubmissionSectionTitle>
                            {isBestOfSubmission ? (
                              <fieldset disabled={isReadOnly} style={{ display: 'contents' }}>
                                <ResourceSubmissionCategory />
                              </fieldset>
                            ) : (
                              <ResourceSubmissionType
                                name="resource_type"
                                resourceTypes={data?.resource_types}
                              />
                            )}
                            {isShown(
                              getRules(formikRef.current?.values['resource_type']),
                              'title'
                            ) ? (
                              <ResourceSubmissionFormFieldWrapper>
                                <ResourceSubmissionFieldTitle>
                                  {getFieldTitle(currentRules, 'title') || 'Add a title'}
                                </ResourceSubmissionFieldTitle>
                                <ResourceSubmissionFormFieldContainer>
                                  <TextInput
                                    {...readOnlyProps}
                                    {...props}
                                    name="title"
                                    placeholder="Make the title punchy and inviting to get people to curious"
                                    required
                                  />
                                </ResourceSubmissionFormFieldContainer>
                              </ResourceSubmissionFormFieldWrapper>
                            ) : null}
                            {isShown(
                              getRules(formikRef.current?.values['resource_type']),
                              'short_description'
                            ) ? (
                              <ResourceSubmissionFormFieldWrapper>
                                <ResourceSubmissionFieldTitle>
                                  {getFieldTitle(currentRules, 'short_description') ||
                                    'One liner'}
                                </ResourceSubmissionFieldTitle>
                                <ResourceSubmissionFormFieldContainer>
                                  <TextInput
                                    {...readOnlyProps}
                                    {...props}
                                    name="short_description"
                                    placeholder="This is the summary that people will see on your submission before they click in for more details"
                                    textarea
                                    required
                                  />
                                </ResourceSubmissionFormFieldContainer>
                              </ResourceSubmissionFormFieldWrapper>
                            ) : null}
                            {isShown(
                              getRules(formikRef.current?.values['resource_type']),
                              'image'
                            ) ? (
                              <ResourceSubmissionFormFieldWrapper>
                                <ResourceSubmissionFieldTitle>
                                  {getFieldTitle(currentRules, 'image') || 'Upload image(s)'}
                                </ResourceSubmissionFieldTitle>
                                <ResourceSubmissionFormFieldContainer>
                                  <FileInput
                                    {...props}
                                    {...disabledProps}
                                    name="image"
                                    additionalFilesName="additional_images"
                                    subtitle={isV2BestOfSubmission
                                      ? `Images should ideally be 1440 x 810 (16:9) and no larger than ${imageMaxSizeBytes / 1000000}MB.`
                                      : 'Ideally images are 1440 x 810 (16:9 ratio) and are a maximum size of 5MB'}
                                    accept={{
                                      'image/jpeg': ['.jpeg', '.jpg', '.png', '.gif'],
                                    }}
                                    customErrors={{
                                      [ErrorCode.TooManyFiles]:
                                        'Looks like you tried to upload too many files at once. You can upload a maximum of 5 in one go. ',
                                      [ErrorCode.FileTooLarge]:
                                        'That file is too large. Try resizing it and uploading it again.',
                                      [ErrorCode.FileInvalidType]:
                                        'We can only accept images (PNGs and JPGs) and GIFs.',
                                    }}
                                    maxSize={imageMaxSizeBytes}
                                    maxFiles={5}
                                    prepopulatedFiles={prePopulatedImages}
                                  />
                                </ResourceSubmissionFormFieldContainer>
                              </ResourceSubmissionFormFieldWrapper>
                            ) : null}
                            {isShown(
                              getRules(formikRef.current?.values['resource_type']),
                              'resource_format_uuid'
                            ) ? (
                              <ResourceSubmissionFormFieldWrapper>
                                <ResourceSubmissionFieldTitle>
                                  {getFieldTitle(currentRules, 'resource_format_uuid') ||
                                    'Select the file type'}
                                </ResourceSubmissionFieldTitle>
                                <ResourceSubmissionFormFieldContainer>
                                  <ResourceSubmissionFieldSubtitle
                                    isError={
                                      (props.errors['resource_format_uuid'] &&
                                        props.touched['resource_format_uuid']) as boolean
                                    }
                                  >
                                    {isRequired(currentRules, 'resource_format_uuid')
                                      ? 'Required'
                                      : 'Optional'}
                                  </ResourceSubmissionFieldSubtitle>
                                  <ResourceSubmissionTypeSelect
                                      {...disabledProps}
                                    onChange={(rf) => {
                                      if (rf.label) {
                                        setResourceFormat(rf.label);
                                        setResource({
                                          ...resource,
                                          resource_format_name: rf.label,
                                        });
                                      }
                                    }}
                                    name="resource_format_uuid"
                                    options={ResourceFormatOptions}
                                    placeholder="Select entry format"
                                    preselectedValue={
                                      resource && resource.resource_format_uuid
                                        ? {
                                            value: resource.resource_format_uuid,
                                            label: resource.resource_format_name,
                                          }
                                        : null
                                    }
                                  />
                                </ResourceSubmissionFormFieldContainer>
                              </ResourceSubmissionFormFieldWrapper>
                            ) : null}
                            {isShown(
                              getRules(formikRef.current?.values['resource_type']),
                              'file'
                            ) ? (
                              <>
                                {[ENTRY_TYPE.PDF, ENTRY_TYPE.IMAGE].includes(
                                  currentResourceFormat?.toLowerCase()
                                ) ? (
                                  <ResourceSubmissionFormFieldWrapper>
                                    <ResourceSubmissionFieldTitle>
                                      {getFieldTitle(currentRules, 'file') || 'Upload File'}
                                    </ResourceSubmissionFieldTitle>
                                    <ResourceSubmissionFormFieldContainer>
                                      {formikRef.current?.errors['file'] &&
                                      formikRef.current?.touched['file'] ? null : (
                                        <ResourceSubmissionFieldSubtitle>
                                          Required
                                        </ResourceSubmissionFieldSubtitle>
                                      )}
                                      <FileInput
                                        {...props}
                                          {...disabledProps}
                                        name="file"
                                        subtitle={isV2BestOfSubmission
                                          ? `Files can be up to ${data?.max_file_size / 1024}MB.`
                                          : `File size should be a maximum size of ${data?.max_file_size / 1024}MB.`}
                                        accept={fileAcceptance}
                                        customErrors={{
                                          [ErrorCode.TooManyFiles]: 'Too many files',
                                          [ErrorCode.FileTooLarge]:
                                            'That file is too large. Try resizing it and uploading it again.',
                                          [ErrorCode.FileInvalidType]: fileInvalidTypeError,
                                        }}
                                        maxSize={data?.max_file_size * 1024}
                                        prepopulatedFiles={prePopulatedFiles}
                                      />
                                    </ResourceSubmissionFormFieldContainer>
                                  </ResourceSubmissionFormFieldWrapper>
                                ) : currentResourceFormat !== null ? (
                                  <ResourceSubmissionFormFieldWrapper>
                                    <ResourceSubmissionFieldTitle>
                                      {getFieldTitle(currentRules, 'link') ||
                                        'Link to where we can see your project / interact with it'}
                                    </ResourceSubmissionFieldTitle>
                                    <ResourceSubmissionFormFieldContainer>
                                      <TextInput
                                          {...readOnlyProps}
                                        {...props}
                                        name="link"
                                        placeholder="https://..."
                                        required
                                      />
                                      <ResourceSubmissionLinkHint
                                        resourceFormat={currentResourceFormat}
                                      />
                                    </ResourceSubmissionFormFieldContainer>
                                  </ResourceSubmissionFormFieldWrapper>
                                ) : null}
                              </>
                            ) : null}
                            {isShown(
                              getRules(formikRef.current?.values['resource_type']),
                              'has_paywall'
                            ) ? (
                              <ResourceSubmissionPaywallAccess disabled={isReadOnly} />
                            ) : null}
                            <ResourceSubmissionSectionTitle>
                              2. What’s this resource about?
                            </ResourceSubmissionSectionTitle>
                            {isShown(
                              getRules(formikRef.current?.values['resource_type']),
                              'long_description'
                            ) ? (
                              <ResourceSubmissionFormFieldWrapper>
                                <ResourceSubmissionFieldTitle>
                                  {isV2BestOfSubmission
                                    ? 'Main description: describe the work, the process and the story'
                                    : getFieldTitle(currentRules, 'long_description') ??
                                      'Main description - describe the work, process and the story'}
                                </ResourceSubmissionFieldTitle>
                                <ResourceSubmissionFormFieldContainer>
                                  <TextInput
                                    {...readOnlyProps}
                                    {...props}
                                    name="long_description"
                                    placeholder="What’s the story behind this project? What were the painpoints?"
                                    textarea
                                    required
                                  />
                                </ResourceSubmissionFormFieldContainer>
                              </ResourceSubmissionFormFieldWrapper>
                            ) : null}
                            {isShown(
                              getRules(formikRef.current?.values['resource_type']),
                              'impact_metrics'
                            ) ? (
                              <ResourceSubmissionFormFieldWrapper>
                                <ResourceSubmissionFieldTitle>
                                  Impact metrics
                                </ResourceSubmissionFieldTitle>
                                <ResourceSubmissionFormFieldContainer>
                                  <ResourceSubmissionFieldSubtitle>
                                    {isRequired(currentRules, 'impact_metrics')
                                      ? 'Required'
                                      : 'Optional'}
                                  </ResourceSubmissionFieldSubtitle>
                                  <TextInput
                                    {...readOnlyProps}
                                    {...props}
                                    name="impact_metrics"
                                    placeholder="If there are quantitative metrics about the work, share them here"
                                    textarea
                                  />
                                </ResourceSubmissionFormFieldContainer>
                              </ResourceSubmissionFormFieldWrapper>
                            ) : null}
                            {isShown(
                              getRules(formikRef.current?.values['resource_type']),
                              'clients_uuids'
                            ) ? (
                              <ResourceSubmissionFormFieldWrapper>
                                {!fromBestOf2025 ? (
                                  <ResourceSubmissionFieldTitle>
                                    {getFieldTitle(currentRules, 'clients_uuids') ||
                                      'Related Clients'}
                                  </ResourceSubmissionFieldTitle>
                                ) : null}
                                <ResourceSubmissionFormFieldContainer>
                                  {!fromBestOf2025 ? (
                                    <>
                                      <ResourceSubmissionFieldSubtitle
                                        isError={
                                          props.errors['clients_uuids'] &&
                                          props.touched['clients_uuids'] &&
                                          !props.values['internal_initiative']
                                        }
                                      >
                                        {props.errors['clients_uuids'] &&
                                        props.touched['clients_uuids'] &&
                                        !props.values['internal_initiative']
                                          ? 'Required. At least one company must be selected as the client'
                                          : !props.values['internal_initiative'] &&
                                            isRequired(currentRules, 'clients_uuids')
                                          ? 'Required'
                                          : 'Optional'}
                                      </ResourceSubmissionFieldSubtitle>
                                      <ResourceSubmissionFieldInitiativeContainer>
                                        <ResourceSubmissionFieldCheckboxWrapper>
                                          <ResourceSubmissionFieldCheckboxContainer>
                                            <ResourceSubmissionFieldCheckbox
                                              type="checkbox"
                                              disabled={isReadOnly}
                                              checked={
                                                !formikRef.current?.values.internal_initiative
                                              }
                                                onChange={() =>
                                                  handleInitiativeCheckboxChange(false)
                                                }
                                            />
                                            <ResourceSubmissionFieldCheckboxBackground>
                                              <CheckboxCheckIcon fill="var(--color-primary)" />
                                            </ResourceSubmissionFieldCheckboxBackground>
                                          </ResourceSubmissionFieldCheckboxContainer>
                                          <ResourceSubmissionFieldCheckboxLabel>
                                            For a client / external initiative
                                          </ResourceSubmissionFieldCheckboxLabel>
                                        </ResourceSubmissionFieldCheckboxWrapper>
                                        <ResourceSubmissionFieldCheckboxWrapper>
                                          <ResourceSubmissionFieldCheckboxContainer>
                                            <ResourceSubmissionFieldCheckbox
                                              type="checkbox"
                                              disabled={isReadOnly}
                                              checked={
                                                formikRef.current?.values.internal_initiative
                                              }
                                                onChange={() =>
                                                  handleInitiativeCheckboxChange(true)
                                                }
                                            />
                                            <ResourceSubmissionFieldCheckboxBackground>
                                              <CheckboxCheckIcon fill="var(--color-primary)" />
                                            </ResourceSubmissionFieldCheckboxBackground>
                                          </ResourceSubmissionFieldCheckboxContainer>
                                          <ResourceSubmissionFieldCheckboxLabel>
                                            Internal to my company / to the kyu Collective
                                          </ResourceSubmissionFieldCheckboxLabel>
                                        </ResourceSubmissionFieldCheckboxWrapper>
                                      </ResourceSubmissionFieldInitiativeContainer>
                                    </>
                                  ) : null}
                                  {!formikRef.current?.values.internal_initiative ||
                                  fromBestOf2025 ? (
                                    <div style={{ marginTop: !fromBestOf2025 ? '24px' : '0px' }}>
                                      <ResourceSubmissionFieldTitle>
                                        {getFieldTitle(currentRules, 'clients_uuids') || 'Client'}
                                      </ResourceSubmissionFieldTitle>
                                      <ResourceSubmissionFormFieldContainer>
                                        <ResourceSubmissionFieldSubtitle>
                                          {!props.values['internal_initiative'] &&
                                          isRequired(currentRules, 'clients_uuids')
                                            ? 'Required'
                                            : 'Optional'}
                                        </ResourceSubmissionFieldSubtitle>
                                        <ClientSelect
                                          name="clients_uuids"
                                            {...disabledProps}
                                          allowCreate
                                          placeholder="Start typing to search for clients"
                                          onChange={handleClientSelectChange}
                                          preselectedValues={clients}
                                        />
                                      </ResourceSubmissionFormFieldContainer>
                                    </div>
                                  ) : null}
                                </ResourceSubmissionFormFieldContainer>
                              </ResourceSubmissionFormFieldWrapper>
                            ) : null}
                            {isShown(
                              getRules(formikRef.current?.values['resource_type']),
                              'skills_uuids'
                            ) ? (
                              <ResourceSubmissionFormFieldWrapper>
                                <ResourceSubmissionFieldTitle>
                                  {getFieldTitle(currentRules, 'skills_uuids') ||
                                    'Skills and tools utilized'}
                                </ResourceSubmissionFieldTitle>
                                <ResourceSubmissionFormFieldContainer>
                                  <ResourceSubmissionFieldSubtitle>
                                    {isRequired(currentRules, 'skills_uuids')
                                      ? 'Required'
                                      : 'Optional'}
                                  </ResourceSubmissionFieldSubtitle>
                                  <SkillSelect
                                    name="skills_uuids"
                                      {...disabledProps}
                                    allowMultiple
                                    allowCreate
                                    placeholder="Start typing to search for skills"
                                    onChange={handleSkillSelectChange}
                                    preselectedValues={skills}
                                  />
                                </ResourceSubmissionFormFieldContainer>
                              </ResourceSubmissionFormFieldWrapper>
                            ) : null}
                            {isShown(
                              getRules(formikRef.current?.values['resource_type']),
                              'project_categories_uuids'
                            ) ? (
                              <ResourceSubmissionFormFieldWrapper>
                                <ResourceSubmissionFieldTitle>
                                  {getFieldTitle(currentRules, 'project_categories_uuids') ||
                                    'How would you categorize the services provided?'}
                                </ResourceSubmissionFieldTitle>
                                <ResourceSubmissionFormFieldContainer>
                                  <ResourceSubmissionFieldSubtitle>
                                    {isRequired(currentRules, 'project_categories_uuids')
                                      ? 'Required'
                                      : 'Optional'}
                                  </ResourceSubmissionFieldSubtitle>
                                  <ProjectCategorySelect
                                    name="project_categories_uuids"
                                      {...disabledProps}
                                    allowMultiple
                                    allowCreate
                                    placeholder="Start typing to search for project categories"
                                    onChange={handleProjectCategorySelectChange}
                                    preselectedValues={projectCategories}
                                  />
                                </ResourceSubmissionFormFieldContainer>
                              </ResourceSubmissionFormFieldWrapper>
                            ) : null}
                            {isShown(
                              getRules(formikRef.current?.values['resource_type']),
                              'tags'
                            ) ? (
                              <ResourceSubmissionFormFieldWrapper>
                                <ResourceSubmissionFieldTitle>
                                  Key Themes
                                </ResourceSubmissionFieldTitle>
                                <ResourceSubmissionFormFieldContainer>
                                  <ResourceSubmissionFieldSubtitle>
                                    {isRequired(currentRules, 'tags') ? 'Required' : 'Optional'}
                                  </ResourceSubmissionFieldSubtitle>
                                    <ResourceSubmissionTagsField
                                      {...props}
                                      {...disabledProps}
                                      name="tags"
                                    />
                                </ResourceSubmissionFormFieldContainer>
                              </ResourceSubmissionFormFieldWrapper>
                            ) : null}
                            <ResourceSubmissionSectionTitle>
                                3. Did you or someone at kyu create this resource or contribute to
                                it?
                            </ResourceSubmissionSectionTitle>
                            {isShown(
                              getRules(formikRef.current?.values['resource_type']),
                              'started_at'
                            ) ||
                            isShown(
                              getRules(formikRef.current?.values['resource_type']),
                              'released_at'
                            ) ? (
                              <ResourceSubmissionFormFieldWrapper>
                                <ResourceSubmissionFieldTitle>
                                  Client work dates
                                </ResourceSubmissionFieldTitle>
                                <ResourceSubmissionFormFieldContainer>
                                  <ResourceSubmissionFieldSubtitle>
                                    Start Date (
                                    {isRequired(currentRules, 'started_at')
                                      ? 'required'
                                      : 'optional'}
                                    )
                                  </ResourceSubmissionFieldSubtitle>
                                  <DateInput
                                    {...props}
                                      {...disabledProps}
                                    name="started_at"
                                    placeholder="Start date"
                                    yearRange={{ start: 1900, end: new Date().getFullYear() }}
                                  />
                                  <ResourceSubmissionFieldSubtitle>
                                    End Date (
                                    {isRequired(currentRules, 'released_at')
                                      ? 'required'
                                      : 'optional'}
                                    )
                                  </ResourceSubmissionFieldSubtitle>
                                    <DateInput
                                      {...props}
                                      {...disabledProps}
                                      name="released_at"
                                      placeholder="End date"
                                    />
                                </ResourceSubmissionFormFieldContainer>
                              </ResourceSubmissionFormFieldWrapper>
                            ) : null}
                            {isShown(
                              getRules(formikRef.current?.values['resource_type']),
                              'contributors_uuids'
                            ) ? (
                              <ResourceSubmissionFormFieldWrapper>
                                <ResourceSubmissionFieldTitle>
                                  Team members
                                </ResourceSubmissionFieldTitle>
                                <ResourceSubmissionFormFieldContainer>
                                  <ResourceSubmissionFieldSubtitle
                                    isError={
                                      (formikRef.current?.errors.contributors_uuids &&
                                        formikRef.current?.touched.contributors_uuids) as boolean
                                    }
                                  >
                                    {formikRef.current?.errors.contributors_uuids &&
                                    formikRef.current?.touched.contributors_uuids
                                      ? 'Required. At least one individual must be selected as contributor.'
                                      : isRequired(currentRules, 'contributors_uuids')
                                      ? `Required. You can add as many team members as you'd like`
                                      : 'Optional'}
                                  </ResourceSubmissionFieldSubtitle>
                                  <PersonSelect
                                    placeholder="Start typing to search for people"
                                      {...disabledProps}
                                    allowMultiple
                                    name="contributors_uuids"
                                    preselectedValues={
                                      currentContributors || [
                                        {
                                          name: `${userData?.first_name} ${userData?.last_name}`,
                                          uuid: userData?.uuid,
                                        },
                                      ]
                                    }
                                  />
                                </ResourceSubmissionFormFieldContainer>
                              </ResourceSubmissionFormFieldWrapper>
                            ) : null}
                            {isShown(
                              getRules(formikRef.current?.values['resource_type']),
                              'companies_uuids'
                            ) ? (
                              <ResourceSubmissionFormFieldWrapper>
                                <ResourceSubmissionFieldTitle>
                                  {isV2BestOfSubmission ? 'kyu Companies' : 'kyu Compani(es)'}
                                </ResourceSubmissionFieldTitle>
                                <ResourceSubmissionFormFieldContainer>
                                  <ResourceSubmissionFieldSubtitle
                                    isError={
                                      (formikRef.current?.errors.companies_uuids &&
                                        formikRef.current?.touched.companies_uuids) as boolean
                                    }
                                  >
                                    {formikRef.current?.errors.companies_uuids &&
                                    formikRef.current?.touched.companies_uuids
                                      ? 'Required. At least one company must be selected as contributor.'
                                      : isRequired(currentRules, 'companies_uuids')
                                      ? 'Required'
                                      : 'Optional'}
                                  </ResourceSubmissionFieldSubtitle>
                                  <CompanySelect
                                    name="companies_uuids"
                                    companyOptions={
                                      bestOfSubmission ? data?.eligible_companies || [] : undefined
                                    }
                                    optionsMessage={
                                      fieldsLoading
                                        ? 'Loading eligible companies…'
                                        : fieldsError || !data?.eligible_companies
                                        ? 'Eligible companies are unavailable. Please reload and try again.'
                                        : 'No companies are eligible to submit for this campaign.'
                                    }
                                      {...disabledProps}
                                    allowMultiple
                                    onChange={handleCompanySelectChange}
                                    preselectedValues={
                                      companies || [
                                        {
                                          name: userData?.kyu_company?.name,
                                          uuid:
                                            userData?.kyu_company?.uuid ??
                                            userData?.kyu_company?.id,
                                        },
                                      ]
                                    }
                                  />
                                  {bestOfSubmission && data?.eligible_companies && companies.some(
                                    (company) => !data.eligible_companies.some((eligible) => eligible.uuid === (company.uuid ?? company.id))
                                  ) ? (
                                    <p role="alert">One or more selected companies are no longer eligible for this campaign. Remove them before saving.</p>
                                  ) : null}
                                </ResourceSubmissionFormFieldContainer>
                                <ResourceSubmissionFieldCheckboxWrapper
                                  $hidden={!canSeePulseCheckbox}
                                  aria-hidden={!canSeePulseCheckbox}
                                  data-testid="pulse-submission-option"
                                >
                                  <ResourceSubmissionFieldCheckboxContainer>
                                    <ResourceSubmissionFieldCheckbox
                                      {...props}
                                      name="pulse_submission"
                                      type="checkbox"
                                      disabled={isReadOnly || !canSeePulseCheckbox}
                                      checked={props.values.pulse_submission}
                                      onChange={() =>
                                        props.setFieldValue(
                                          'pulse_submission',
                                          !props.values.pulse_submission
                                        )
                                      }
                                    />
                                    <ResourceSubmissionFieldCheckboxBackground>
                                      <CheckboxCheckIcon fill="var(--color-primary)" />
                                    </ResourceSubmissionFieldCheckboxBackground>
                                  </ResourceSubmissionFieldCheckboxContainer>
                                  <ResourceSubmissionFieldCheckboxLabel>
                                    Mark as a kyu Pulse submission
                                  </ResourceSubmissionFieldCheckboxLabel>
                                </ResourceSubmissionFieldCheckboxWrapper>
                              </ResourceSubmissionFormFieldWrapper>
                            ) : null}
                            {isBestOfSubmission ||
                            isShown(
                              getRules(formikRef.current?.values['resource_type']),
                              'internal_share_status'
                            ) ||
                            isShown(
                              getRules(formikRef.current?.values['resource_type']),
                              'external_client_share_status'
                            ) ? (
                              <>
                                <ResourceSubmissionSectionTitle>
                                  4. Permissions
                                </ResourceSubmissionSectionTitle>
                                <ResourceSubmissionPermissions
                                  data={data}
                                  disabled={isReadOnly}
                                  bestOfYear={bestOfYear}
                                />
                              </>
                            ) : null}
                          </ResourceSubmissionFormFieldsWrapper>
                          <ResourceSubmissionCardContainer>
                              {existingResource && !bestOfSubmission ? (
                              <ResourceSubmissionFieldUnpublishWrapper>
                                <ResourceSubmissionFieldUnpublishTitle>
                                  {draftUuids.has(existingResource.uuid)
                                    ? 'I want to publish this resource.'
                                    : 'I want to unpublish this resource.'}
                                </ResourceSubmissionFieldUnpublishTitle>
                                <ResourceSubmissionFieldUnpublishButton
                                  onClick={() =>
                                    draftUuids.has(existingResource.uuid)
                                      ? setIsPublishDialogOpen(true)
                                      : setIsUnpublishDialogOpen(true)
                                  }
                                >
                                  {isUnpublishing || isPublishing ? (
                                    <Spinner fill="var(--color-error)" />
                                  ) : (
                                    <ResourceSubmissionFieldUnpublishButtonLabel>
                                      {draftUuids.has(existingResource.uuid)
                                        ? 'PUBLISH'
                                        : 'UNPUBLISH'}
                                    </ResourceSubmissionFieldUnpublishButtonLabel>
                                  )}
                                </ResourceSubmissionFieldUnpublishButton>
                              </ResourceSubmissionFieldUnpublishWrapper>
                            ) : null}
                            <ResourceCard
                              resource={{
                                  best_of_year: bestOfYear
                                    ? { year: bestOfYear, status: props.values.draft ? 'draft' : 'submitted' }
                                  : null,
                                uuid: '',
                                slug: 'slug',
                                submitted_at: props.values?.started_at || null,
                                title: props.values?.title || 'Preview of your submission',
                                link: props.values?.link,
                                image:
                                  formikRef.current?.values.image?.[0]?.base64 ||
                                  '/images/meta-image.jpg' ||
                                  null,
                                tags: formikRef.current?.values.tags || [],
                                has_paywall: false,
                                submitter:
                                  formikRef.current?.values.show_contributor &&
                                  Object.keys(userData).length > 0
                                    ? userData
                                    : null,
                                short_description: props.values?.short_description || null,
                                clients: clients || [],
                                likes: {
                                  count: 0,
                                  people: {
                                    samples: [],
                                    uuids: [],
                                  },
                                },
                                contributors: {
                                  people: currentContributors || null,
                                  companies: companies || null,
                                  communities: [],
                                },
                              }}
                              overrideResourceClick={() => {}}
                              alwaysShowDescription
                              showClients
                            />
                          </ResourceSubmissionCardContainer>
                        </ResourceSubmissionFormWrapper>
                        </div>
                      </ResourceSubmissionContainer>
                    ) : (
                      <ResourceSubmissionPreview />
                    )}
                    {!isMobile && !isReadOnly ? (
                      <ResourceSubmissionFieldSubmitButtonContainer data-testid="resource-submission-actions">
                        {bestOfSubmission?.errorMessage ? (
                          <ResourceSubmissionSubmitError role="alert">
                            {bestOfSubmission.errorMessage}
                          </ResourceSubmissionSubmitError>
                        ) : null}
                        <ResourceSubmissionFieldSubmitButton
                          onClick={handleSubmitClick}
                          disabled={isPeopleSamplesLoading || isLoading}
                          type="submit"
                        >
                          {!isLoading ? (
                            <ResourceSubmissionFieldSubmitButtonLabel>
                              {!isPreviewing
                                ? 'PREVIEW'
                                : bestOfSubmission?.state === 'submitted'
                                ? 'SUBMIT CHANGES'
                                : 'SUBMIT'}
                            </ResourceSubmissionFieldSubmitButtonLabel>
                          ) : (
                            <Spinner />
                          )}
                        </ResourceSubmissionFieldSubmitButton>
                      </ResourceSubmissionFieldSubmitButtonContainer>
                    ) : null}
                  </>
                );
              }}
            </Formik>
          </ResourceSubmissionWrapper>
        </ResourceSubmissionStepsWrapper>
        {isUnpublishDialogOpen
          ? ReactDOM.createPortal(
              <ResourceSubmissionUnpublishDialog
                onConfirm={handleUnpublishResource}
                onCancel={() => setIsUnpublishDialogOpen(false)}
                isPublish={false}
              />,
              document.getElementById('root')
            )
          : null}
        {isPublishDialogOpen
          ? ReactDOM.createPortal(
              <ResourceSubmissionUnpublishDialog
                onConfirm={handlePublishResource}
                onCancel={() => setIsPublishDialogOpen(false)}
                isPublish={true}
              />,
              document.getElementById('root')
            )
          : null}
        {isMobile && !isReadOnly ? (
          <ResourceSubmissionFieldSubmitButtonContainer data-testid="resource-submission-actions">
            {bestOfSubmission?.errorMessage ? (
              <ResourceSubmissionSubmitError role="alert">
                {bestOfSubmission.errorMessage}
              </ResourceSubmissionSubmitError>
            ) : null}
            <ResourceSubmissionFieldSubmitButton
              onClick={handleSubmitClick}
              disabled={isPeopleSamplesLoading || isLoading}
              type="submit"
            >
              {!isLoading ? (
                <ResourceSubmissionFieldSubmitButtonLabel>
                  {!isPreviewing
                    ? 'PREVIEW'
                    : bestOfSubmission?.state === 'submitted'
                    ? 'SUBMIT CHANGES'
                    : 'SUBMIT'}
                </ResourceSubmissionFieldSubmitButtonLabel>
              ) : (
                <Spinner />
              )}
            </ResourceSubmissionFieldSubmitButton>
          </ResourceSubmissionFieldSubmitButtonContainer>
        ) : null}
      </ResourceSubmissionContext.Provider>
    </ThemeProvider>
  );
}
